// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface IMountedInChunksOptions {
  itemCount: number;
  // How many more items each step mounts.
  chunk: number;
  // Where to start: the depth a previous mount had reached, when a surface restores it.
  initialCount?: number;
}

export interface IMountedInChunks {
  // Render items.slice(0, mountedCount).
  mountedCount: number;
  // Put on an element below the last mounted item; the next chunk mounts when it scrolls into
  // view. Render it only while items remain.
  sentinelRef: (node: Element | null) => void;
  // Mounts up to and past this index if it is not mounted yet. Answers whether it already was,
  // so a caller that needs the item's element can act now or wait for the next commit.
  ensureMounted: (index: number) => boolean;
  // The depth reached, readable from an unmount cleanup (state is stale there).
  mountedCountRef: React.MutableRefObject<number>;
}

// Incremental mounting, shared by the map rail and the dashboard's grid and list: a large
// organisation's whole portfolio in one commit (cards, observers, menus) blocks the main thread
// for seconds, and every mounted card asks for a thumbnail. The first chunk renders at once; a
// sentinel below the list mounts the rest as the user scrolls. Nothing unmounts, so every
// card's position is what the browser lays out and scrolling stays the browser's alone.
export const useMountedInChunks = ({ itemCount, chunk, initialCount }: IMountedInChunksOptions): IMountedInChunks => {
  const [mountedCount, setMountedCount] = useState(() => initialCount || chunk);
  const mountedCountRef = useRef(mountedCount);
  mountedCountRef.current = mountedCount;

  const { ref: sentinelRef, inView } = useInView();
  useEffect(() => {
    if (inView) setMountedCount((current) => Math.min(current + chunk, itemCount || chunk));
  }, [inView, itemCount, chunk]);

  const ensureMounted = useCallback((index: number): boolean => {
    if (index < mountedCountRef.current) return true;
    setMountedCount(index + chunk);
    return false;
  }, [chunk]);

  return { mountedCount, sentinelRef, ensureMounted, mountedCountRef };
};

export default useMountedInChunks;
